import { debounce } from 'lodash-es';
import { MAX_INACTIVE_TIME_MINUTES, UPDATE_INTERVAL_MS } from '@/common/constants/ui.constants';
import { mapGetters } from 'vuex';
import { mapState, mapStores } from 'pinia';
import useAuthStore from '@/common/store/pinia/auth';
import { useStorage } from '@vueuse/core';
import useAppStore from '@/common/store/pinia/app';

export default {
  data() {
    return {
      debounced: null,
      intervalCounter: null,
      notificationInstance: null,
      storageState: null,
    };
  },
  beforeDestroy() {
    this.stopLeaveCounter();
  },
  mounted() {
    this.storageState = useStorage(this.localStorageVar, {});
  },
  computed: {
    ...mapStores(useAuthStore),
    ...mapState(useAppStore, ['appName']),
    ...mapGetters({
      preventReloadProgressNotifications: 'notifications/preventReloadProgressNotifications',
    }),
    localStorageVar() {
      return `leaveTime-${this.appName}`;
    }
  },
  watch: {
    isLoggedIn: {
      handler(val, oldVal) {
        if (typeof window === 'undefined') {
          return;
        }
        if (val) {
          // VSTOR-46993: on page open/reload need to restore the counter
          if (typeof oldVal !== 'undefined') {
            this.refreshCounter();
          }
          this.startLeaveCounter();
        } else {
          this.stopLeaveCounter();
        }
      },
      immediate: true
    },
    isSuspended(val) {
      if (val) {
        this.stopLeaveCounter();
      }
    },
  },
  methods: {
    signOut() {
      this.stopLeaveCounter();
      this.authStore.signOut()
        .then(() => {
          this.notificationInstance = this.$notify({
            skipStore: true,
            title: this.$root.$t('You were logged out of the system.'),
            message: this.$root.$t('Session was automatically closed after {minutes} minutes of inactivity.', { minutes: MAX_INACTIVE_TIME_MINUTES }),
            duration: 0
          });
        });
    },
    startLeaveCounter() {
      if (typeof this.notificationInstance?.close === 'function') {
        this.notificationInstance.close();
      }
      this.increaseCounter();
      this.debounced = debounce(this.refreshCounter, 300, { leading: true });
      window.addEventListener('mousemove', this.debounced);
      window.addEventListener('keypress', this.debounced);
      window.addEventListener('click', this.debounced);
    },
    stopLeaveCounter() {
      clearTimeout(this.intervalCounter);
      this.intervalCounter = null;
      this.fillCounter();
      window.removeEventListener('mousemove', this.debounced);
      window.removeEventListener('keypress', this.debounced);
      window.removeEventListener('click', this.debounced);
    },
    refreshCounter() {
      // We store leave time in localstorage because of syncing between opened tabs
      this.saveToLocalStorage(0);
    },
    fillCounter() {
      // Set maximum time in leave counter
      this.saveToLocalStorage(MAX_INACTIVE_TIME_MINUTES * 60);
    },
    increaseCounter() {
      this.intervalCounter = setTimeout(() => {
        const { value, timestamp } = this.storageState;
        const leaveTime = (value || 0) + Math.floor((Date.now() - timestamp) / 1000);

        // In case when several tabs are open only one counter should increase the value
        if (!timestamp || Date.now() - timestamp >= UPDATE_INTERVAL_MS) {
          this.saveToLocalStorage(leaveTime);
        }

        if (leaveTime >= MAX_INACTIVE_TIME_MINUTES * 60 && this.preventReloadProgressNotifications.length === 0) {
          this.signOut();
        } else {
          this.increaseCounter();
        }
      }, UPDATE_INTERVAL_MS);
    },
    saveToLocalStorage(value) {
      this.storageState = { value, timestamp: Date.now() };
    }
  }
};
